import type { CSSProperties } from 'react';
import {
  appendSchedulePastOpacity,
  getLessonSlotStylePropsForLesson,
  isScheduleOccurrencePast,
  POSTPONED_SCHEDULE_SLOT_CLASSES,
  REPLACEMENT_SCHEDULE_SLOT_CLASSES,
  type AgeGroupCalendarColorLookup,
} from '@/utils/lessonSlotColors';
import {
  isLessonOccurrenceCancelledOnDate,
  type ScheduleOccurrence,
} from '@/utils/scheduleOccurrences';

export interface ScheduleOccurrenceChipProps {
  className: string;
  style?: CSSProperties;
}

/**
 * Shared chip styling for lessons, court hire, and closures across day/week/month views.
 */
export function getScheduleOccurrenceChipProps(
  occ: ScheduleOccurrence,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    isReplacement?: boolean;
  }
): ScheduleOccurrenceChipProps {
  const isPast = isScheduleOccurrencePast(occ.date, occ.endTime);
  const isClosure = occ.sourceType === 'closure';
  const isCourtHire = occ.sourceType === 'court_hire';
  const isPlayEvent = occ.sourceType === 'play_event';

  if (isClosure) {
    return {
      className:
        'border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 opacity-90',
    };
  }

  if (isCourtHire) {
    return {
      className:
        'border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/30 text-teal-900 dark:text-teal-100',
    };
  }

  if (isPlayEvent) {
    if (occ.status === 'cancelled') {
      return {
        className:
          'border border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 line-through opacity-80',
      };
    }
    return {
      className:
        'border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100',
    };
  }

  if (isLessonOccurrenceCancelledOnDate(occ.lesson, occ.date, occ.status)) {
    return {
      className:
        'border border-red-300 dark:border-red-700 border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 line-through opacity-75',
    };
  }

  if (occ.status === 'postponed') {
    return { className: appendSchedulePastOpacity(POSTPONED_SCHEDULE_SLOT_CLASSES, isPast) };
  }

  if (options?.isReplacement) {
    return { className: appendSchedulePastOpacity(REPLACEMENT_SCHEDULE_SLOT_CLASSES, isPast) };
  }

  if (occ.isPrivate) {
    const classes = occ.isStandalonePrivate
      ? 'border border-orange-300 dark:border-orange-700 border-l-4 border-l-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100'
      : 'border border-purple-300 dark:border-purple-700 border-l-4 border-l-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100';
    return { className: appendSchedulePastOpacity(classes, isPast) };
  }

  if (occ.isCamp) {
    const classes =
      'border border-teal-300 dark:border-teal-700 border-l-4 border-l-teal-500 bg-teal-50 dark:bg-teal-900/30 text-teal-900 dark:text-teal-100';
    return { className: appendSchedulePastOpacity(classes, isPast) };
  }

  const styleProps = getLessonSlotStylePropsForLesson(occ.lesson, options?.ageGroupById, { isPast });
  return {
    className: appendSchedulePastOpacity(styleProps.className, isPast),
    style: styleProps.style,
  };
}

/** Day view slot classes including cancelled-lesson amber treatment. */
export function getDayScheduleOccurrenceSlotClasses(
  occ: ScheduleOccurrence,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    editable?: boolean;
    onClick?: boolean;
    isDragging?: boolean;
  }
): string {
  const isCancelled = occ.status === 'cancelled';
  const chipProps = getScheduleOccurrenceChipProps(occ, options);

  const dragClasses = options?.isDragging
    ? 'opacity-50 ring-2 ring-blue-400 ring-offset-2 z-50'
    : '';

  const interactionClasses =
    options?.editable && !isCancelled && occ.sourceType === 'lesson'
      ? 'cursor-grab active:cursor-grabbing'
      : options?.onClick && !isCancelled
        ? 'cursor-pointer'
        : '';

  const cancelledClasses = isCancelled
    ? 'border border-amber-300 dark:border-amber-700 border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 opacity-80'
    : chipProps.className;

  return `absolute rounded p-1.5 text-xs select-none overflow-hidden flex flex-col min-h-0 min-w-0 leading-tight ${dragClasses} ${cancelledClasses} ${interactionClasses}`;
}

export function getDayScheduleOccurrenceSlotStyle(
  occ: ScheduleOccurrence,
  options?: {
    ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
    isReplacement?: boolean;
  }
): CSSProperties | undefined {
  if (occ.status === 'cancelled' || occ.sourceType === 'closure' || occ.sourceType === 'court_hire') {
    return undefined;
  }
  return getScheduleOccurrenceChipProps(occ, options).style;
}
